import { Badge, Group, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getTravelDocumentKindsOptions } from '../../model/TravelRequestQuery';
import {
  TravelActivityType,
  TravelFileKind,
  TravelRequest,
  hasTravelMobilitySupport,
  requiredTravelSubmissionKinds,
  travelFileKindLabel,
} from '../../model/TravelRequestTypes';

interface TravelRequestDocumentChecklistProps {
  request: TravelRequest;
}

export interface DocumentChecklistRow {
  kind: TravelFileKind;
  label?: string;
  required: boolean;
  uploaded: boolean;
  hint: 'documentRequired' | 'documentExpected' | 'documentReturnRequired';
}

interface DocumentChecklistProps {
  title: string;
  rows: DocumentChecklistRow[];
  guidance?: string | null;
  activityType: TravelActivityType;
}

function rowColor(row: DocumentChecklistRow) {
  if (row.uploaded) {
    return 'green';
  }

  return row.required ? 'red' : 'gray';
}

export function DocumentChecklist({ title, rows, guidance, activityType }: DocumentChecklistProps) {
  const { t } = useTranslation();

  return (
    <Stack gap={4}>
      <Text
        size="sm"
        fw="500"
      >
        {title}
      </Text>
      {guidance && (
        <Text
          size="xs"
          c="dimmed"
        >
          {guidance}
        </Text>
      )}
      {rows.map(row => (
        <Group
          key={row.label ?? row.kind}
          gap="xs"
        >
          <Badge
            size="sm"
            variant="light"
            color={rowColor(row)}
          >
            {t(row.uploaded ? 'travel.requests.view.documentUploaded' : 'travel.requests.view.documentMissing')}
          </Badge>
          <Text size="sm">{row.label ?? travelFileKindLabel(t, row.kind, activityType)}</Text>
          <Text
            size="xs"
            c="dimmed"
          >
            {t(`travel.requests.view.${row.hint}`)}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

export function TravelRequestDocumentChecklist({ request }: TravelRequestDocumentChecklistProps) {
  const { t } = useTranslation();
  const { data } = useQuery(getTravelDocumentKindsOptions(request.activityType, hasTravelMobilitySupport(request)));

  const required = requiredTravelSubmissionKinds(request, data);
  const expected = data?.expected ?? [];

  if (data == null || (required.length === 0 && expected.length === 0)) {
    return null;
  }

  const uploadedKinds = new Set(request.files.map(file => file.kind));
  const rows: DocumentChecklistRow[] = [
    ...required.map(kind => ({
      kind,
      required: true,
      uploaded: uploadedKinds.has(kind),
      hint: 'documentRequired' as const,
    })),
    ...expected.map(kind => ({
      kind,
      required: false,
      uploaded: uploadedKinds.has(kind),
      hint: 'documentExpected' as const,
    })),
  ];

  return (
    <DocumentChecklist
      title={t('travel.requests.view.documentsChecklist')}
      rows={rows}
      guidance={data.approvalGuidance}
      activityType={request.activityType}
    />
  );
}
